/**
 * Per-campaign list-performance-tags queries for the Prepared Drops list.
 * One small query per campaign — identical key and params to the stats tab's
 * campaign-scoped query, so client and server caches are shared — merged into
 * a lookup keyed by campaign wiserId for per-row tag resolution.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { buildListTagQueryParams } from '@/features/smswiser-campaigns/utils/listTagQueryParams';

import {
  dashboardParamsKey,
  fetchListPerformanceTags,
  smswiserKeys,
  useDropsViewPerfTagsEnabled,
  type ListPerformanceTagsResponse,
} from './useSMSWiser';

const STALE_MS = 2 * 60 * 60 * 1000; // match useListPerformanceTags

export interface ListPerfTagsByCampaign {
  /** Response per campaign wiserId — resolve a drop's tags in ITS campaign scope. */
  byCampaign: Record<number, ListPerformanceTagsResponse | undefined>;
  /** Rule colors merged across the loaded campaigns. */
  tagColors: Record<string, string>;
  isLoading: boolean;
}

export function useListPerfTagsByCampaign(
  countryKey: string,
  campaignIds: number[],
): ListPerfTagsByCampaign {
  const queriesEnabled = useDropsViewPerfTagsEnabled(
    countryKey,
    campaignIds.length > 0,
  );
  const params = useMemo(() => buildListTagQueryParams(), []);
  return useQueries({
    queries: campaignIds.map((id) => ({
      queryKey: [
        ...smswiserKeys.country(countryKey),
        'list-performance-tags',
        [id],
        dashboardParamsKey(params),
      ],
      queryFn: () => fetchListPerformanceTags(countryKey, [id], params),
      enabled: queriesEnabled,
      staleTime: STALE_MS,
      gcTime: STALE_MS,
    })),
    combine: (results) => {
      const byCampaign: Record<number, ListPerformanceTagsResponse | undefined> =
        {};
      const tagColors: Record<string, string> = {};
      campaignIds.forEach((id, i) => {
        const data = results[i]?.data;
        byCampaign[id] = data;
        Object.assign(tagColors, data?.tagColors);
      });
      return {
        byCampaign,
        tagColors,
        isLoading: results.some((r) => r.isLoading),
      };
    },
  });
}
